import React, { useState, useEffect, useCallback } from "react";
import { withTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import { format, subDays, startOfDay, endOfDay, parseISO } from "date-fns";
import MDSpinner from "react-md-spinner";

import myConfig from "../../configs/config";
import DateTime from "../../components/DateTime";
import BackButton from "../../components/BackButton";
import PolygonMap from "../../components/Polygon_map";

import "./AttendanceHistory.css";

function LocationHistory(props) {
  const params = useParams();
  const farmId = params.farm_id;

  const [workers, setWorkers] = useState([]);
  const [locationData, setLocationData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(subDays(new Date(), 1));
  const [endDate, setEndDate] = useState(new Date());
  const [selectedWorker, setSelectedWorker] = useState("");
  const [mapData, setMapData] = useState(null);
  const [operatorInfo, setOperatorInfo] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [returnedCount, setReturnedCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [limit] = useState(1000);
  const [offset, setOffset] = useState(0);
  const [activityType, setActivityType] = useState("");
  const [orderBy] = useState("asc"); // Sempre em ordem cronológica

  // Fetch workers
  const fetchWorkers = useCallback(async () => {
    try {
      const WORKER_PROFILE_URL = `${myConfig.BE_URL}/api/attendance/worker-profiles/?farm_id=${farmId}&is_active=true`;

      const response = await axios({
        baseURL: WORKER_PROFILE_URL,
        method: "GET",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("client-token")}`,
        },
      });

      const data = response.data;
      const workersList = Array.isArray(data) ? data : [];
      setWorkers(workersList);

      // Selecionar automaticamente o primeiro trabalhador se houver
      if (workersList.length > 0 && !selectedWorker) {
        setSelectedWorker(workersList[0].id.toString());
      } else if (workersList.length === 0) {
        setLoading(false);
      }
    } catch (error) {
      console.error("Error fetching workers:", error);
      toast.error(props.t("Falha ao carregar trabalhadores"), {
        position: "bottom-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      setLoading(false);
    }
  }, [farmId, props, selectedWorker]);

  // Fetch location history
  const fetchLocationHistory = useCallback(async () => {
    if (!selectedWorker) {
      toast.info(props.t("Selecione um trabalhador"), {
        position: "bottom-right",
        autoClose: 3000,
      });
      return;
    }

    try {
      setLoading(true);
      const fromDate = format(
        startOfDay(startDate),
        "yyyy-MM-dd'T'HH:mm:ss'Z'"
      );
      const toDate = format(endOfDay(endDate), "yyyy-MM-dd'T'HH:mm:ss'Z'");

      // Get selected worker to extract CPF or ID
      const selectedWorkerData = workers.find(
        (w) => w.id === parseInt(selectedWorker)
      );

      if (!selectedWorkerData) {
        toast.error(props.t("Trabalhador não encontrado"), {
          position: "bottom-right",
          autoClose: 3000,
        });
        setLoading(false);
        return;
      }

      const LOCATION_HISTORY_URL = `${myConfig.BE_URL}/api/attendance/location-history/`;

      const requestBody = {
        operator_id: selectedWorkerData.id,
        start_date: fromDate,
        end_date: toDate,
        limit: limit,
        offset: offset,
        order_by: orderBy,
      };

      // Add CPF if available
      if (selectedWorkerData.cpf) {
        requestBody.cpf = selectedWorkerData.cpf;
      }

      // Add activity type filter if selected
      if (activityType) {
        requestBody.activity_type = activityType;
      }

      const response = await axios({
        url: LOCATION_HISTORY_URL,
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("client-token")}`,
          "Content-Type": "application/json",
        },
        data: requestBody,
      });

      const data = response.data;

      if (data.success && data.locations && data.locations.length > 0) {
        // Se offset > 0, acumular dados; senão, substituir
        const newLocationData =
          offset > 0 ? [...locationData, ...data.locations] : data.locations;

        setLocationData(newLocationData);
        setOperatorInfo(data.operator);
        setTotalCount(data.total_count || 0);
        setReturnedCount(newLocationData.length);
        setHasMore(data.has_more || false);
        processMapData(newLocationData);
      } else {
        setLocationData([]);
        setMapData(null);
        setOperatorInfo(null);
        setTotalCount(0);
        setReturnedCount(0);
        setHasMore(false);
        toast.info(
          props.t(
            "Nenhum dado de localização encontrado para o período selecionado"
          ),
          {
            position: "bottom-right",
            autoClose: 3000,
          }
        );
      }

      setLoading(false);
    } catch (error) {
      console.error("Error fetching location history:", error);
      toast.error(props.t("Falha ao carregar histórico de localização"), {
        position: "bottom-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      setLoading(false);
    }
  }, [
    startDate,
    endDate,
    selectedWorker,
    farmId,
    props,
    workers,
    limit,
    offset,
    activityType,
    orderBy,
  ]);

  // Process map data
  const processMapData = useCallback(
    (locations) => {
      if (!locations || locations.length === 0) {
        setMapData(null);
        return;
      }

      // Calculate center (average of all positions)
      const avgLat =
        locations.reduce((sum, loc) => sum + loc.latitude, 0) /
        locations.length;
      const avgLng =
        locations.reduce((sum, loc) => sum + loc.longitude, 0) /
        locations.length;

      // Create polyline path
      const path = locations.map((loc) => ({
        lat: loc.latitude,
        lng: loc.longitude,
        time: loc.timestamp,
        speed: loc.speed || 0,
      }));

      // Create lines array with color based on speed
      // Only add lines if we have more than one point
      const lines =
        locations.length > 1
          ? [
              {
                path: path,
                color: "#007bff",
                strokeWeight: 3,
              },
            ]
          : [];

      // Create stops for significant points (first and last location, or low speed points)
      const stops = [];

      // Add first point as entry
      if (locations.length > 0) {
        stops.push({
          lat: locations[0].latitude,
          lng: locations[0].longitude,
          time: locations[0].timestamp,
          type: "entry",
          title: props.t("Início do trajeto"),
        });
      }

      // Add last point as exit
      if (locations.length > 1) {
        const lastLoc = locations[locations.length - 1];
        stops.push({
          lat: lastLoc.latitude,
          lng: lastLoc.longitude,
          time: lastLoc.timestamp,
          type: "exit",
          title: props.t("Fim do trajeto"),
        });
      }

      setMapData({
        centroidLat: avgLat,
        centroidLng: avgLng,
        lines: locations.length > 1 ? lines : "",
        stops: stops,
      });
    },
    [props]
  );

  // Initial load
  useEffect(() => {
    document.title = "Agriconnected - Histórico de Localização";
    fetchWorkers();
  }, [fetchWorkers]);

  // Reset offset and data when filters change
  useEffect(() => {
    if (selectedWorker) {
      setOffset(0);
      setLocationData([]);
      setMapData(null);
    }
  }, [selectedWorker, startDate, endDate, activityType]);

  // Fetch data automatically when worker is selected for the first time
  useEffect(() => {
    if (selectedWorker && workers.length > 0 && offset === 0) {
      fetchLocationHistory();
    }
  }, [selectedWorker]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch data when offset changes (pagination)
  useEffect(() => {
    if (selectedWorker && offset > 0) {
      fetchLocationHistory();
    }
  }, [offset]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleWorkerChange = (e) => {
    setSelectedWorker(e.target.value);
    setOffset(0); // Reset pagination when changing worker
    setLocationData([]); // Clear previous data
    setMapData(null);
  };

  const formatTimestamp = (timestamp) => {
    try {
      return format(parseISO(timestamp), "dd/MM/yyyy HH:mm:ss");
    } catch {
      return timestamp;
    }
  };

  return (
    <div id="attendance_history_container">
      <div className="x_title">
        <BackButton to={`/${farmId}/operator`} label={props.t("Voltar")} />
        <h2>{props.t("Histórico de Localização do Operador")}</h2>
        <div className="clearfix"></div>
      </div>

      {loading && workers.length === 0 ? (
        <div
          style={{ marginTop: "50px", marginLeft: "50%", minHeight: "200px" }}
        >
          <MDSpinner />
        </div>
      ) : workers.length === 0 ? (
        <div style={{ textAlign: "center", marginTop: "50px" }}>
          <h4>{props.t("Não há trabalhadores cadastrados nesta fazenda.")}</h4>
          <p style={{ color: "#666", marginTop: "15px" }}>
            {props.t(
              "Cadastre trabalhadores para visualizar o histórico de localização."
            )}
          </p>
        </div>
      ) : (
        <>
          {/* Filters */}
          <div className="history-filters">
            <div className="filter-row">
              <div className="form-group">
                <label htmlFor="worker_select">
                  {props.t("Selecionar Trabalhador")} *
                </label>
                <select
                  className="form-control"
                  value={selectedWorker}
                  onChange={handleWorkerChange}
                  id="worker_select"
                >
                  <option value="">
                    {props.t("-- Selecione um Trabalhador --")}
                  </option>
                  {workers.map((worker) => (
                    <option key={worker.id} value={worker.id}>
                      {worker.operator_name.trim()}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="start_date">{props.t("Data Início")}</label>
                <DateTime
                  value={startDate}
                  onChange={(date) => setStartDate(date)}
                  id="start_date"
                />
              </div>
              <div className="form-group">
                <label htmlFor="end_date">{props.t("Data Fim")}</label>
                <DateTime
                  value={endDate}
                  onChange={(date) => setEndDate(date)}
                  id="end_date"
                />
              </div>
              <div className="form-group">
                <label>&nbsp;</label>
                <button
                  className="btn btn-primary"
                  onClick={fetchLocationHistory}
                  style={{ width: "100%" }}
                  disabled={!selectedWorker}
                >
                  {props.t("Buscar Localização")}
                </button>
              </div>
            </div>

            {/* Advanced Filters */}
            <div className="filter-row" style={{ marginTop: "15px" }}>
              <div className="form-group">
                <label htmlFor="activity_type">
                  {props.t("Tipo de Atividade")}
                </label>
                <select
                  className="form-control"
                  value={activityType}
                  onChange={(e) => setActivityType(e.target.value)}
                  id="activity_type"
                >
                  <option value="">{props.t("Todos")}</option>
                  <option value="still">{props.t("Parado")}</option>
                  <option value="walking">{props.t("Andando")}</option>
                  <option value="running">{props.t("Correndo")}</option>
                  <option value="on_bicycle">{props.t("De Bicicleta")}</option>
                  <option value="in_vehicle">{props.t("Em Veículo")}</option>
                </select>
              </div>
            </div>
          </div>

          {loading ? (
            <div
              style={{
                marginTop: "50px",
                marginLeft: "50%",
                minHeight: "200px",
              }}
            >
              <MDSpinner />
            </div>
          ) : (
            <>
              {/* Operator Info */}
              {operatorInfo && (
                <div
                  style={{
                    marginTop: "20px",
                    padding: "15px",
                    backgroundColor: "#f8f9fa",
                    borderRadius: "8px",
                    border: "1px solid #e0e0e0",
                  }}
                >
                  <h4 style={{ marginTop: 0, marginBottom: "10px" }}>
                    {props.t("Informações do Operador")}
                  </h4>
                  <div
                    style={{ display: "flex", gap: "30px", flexWrap: "wrap" }}
                  >
                    <div>
                      <strong>{props.t("Nome")}:</strong> {operatorInfo.name}
                    </div>
                    {operatorInfo.cpf && (
                      <div>
                        <strong>{props.t("CPF")}:</strong> {operatorInfo.cpf}
                      </div>
                    )}
                    <div>
                      <strong>{props.t("Total de registros")}:</strong>{" "}
                      {totalCount}
                    </div>
                    <div>
                      <strong>{props.t("Exibindo")}:</strong> {returnedCount}
                    </div>
                    {hasMore && (
                      <div style={{ color: "#ff9800" }}>
                        <strong>
                          {props.t("⚠ Há mais registros disponíveis")}
                        </strong>
                      </div>
                    )}
                  </div>

                  {/* Pagination Controls */}
                  {hasMore && (
                    <div
                      style={{
                        marginTop: "15px",
                        textAlign: "center",
                      }}
                    >
                      <button
                        className="btn btn-primary"
                        onClick={() => {
                          setOffset(offset + limit);
                        }}
                        disabled={loading}
                        style={{ minWidth: "200px" }}
                      >
                        {loading ? (
                          <span>
                            <MDSpinner
                              size={20}
                              style={{
                                display: "inline-block",
                                marginRight: "8px",
                              }}
                            />
                            {props.t("Carregando...")}
                          </span>
                        ) : (
                          props.t("Carregar Mais Registros")
                        )}
                      </button>
                      <div
                        style={{
                          marginTop: "10px",
                          color: "#666",
                          fontSize: "14px",
                        }}
                      >
                        {props.t("Exibindo")} {returnedCount} {props.t("de")}{" "}
                        {totalCount} {props.t("registros")}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Map Section */}
              {mapData && (
                <div style={{ marginTop: "20px" }}>
                  <div className="x_panel">
                    <div className="x_title">
                      <h3>{props.t("Mapa de Rastreamento")}</h3>
                      <div className="clearfix"></div>
                    </div>
                    <div className="x_content">
                      <div style={{ height: "600px", width: "100%" }}>
                        <PolygonMap
                          map={mapData}
                          map_salt={Date.now()}
                          t={props.t}
                          landplots={[]}
                          equipment={{
                            machine_status: "online",
                            traccar_ico: "person",
                          }}
                        />
                      </div>
                      <div style={{ marginTop: "15px" }}>
                        <p>
                          <strong>{props.t("Total de pontos")}:</strong>{" "}
                          {locationData.length}
                        </p>
                        <div
                          style={{
                            display: "flex",
                            gap: "20px",
                            marginTop: "10px",
                          }}
                        >
                          <div
                            style={{ display: "flex", alignItems: "center" }}
                          >
                            <div
                              style={{
                                width: "20px",
                                height: "3px",
                                backgroundColor: "#007bff",
                                marginRight: "8px",
                              }}
                            ></div>
                            <span>{props.t("Trajeto do operador")}</span>
                          </div>
                          <div
                            style={{ display: "flex", alignItems: "center" }}
                          >
                            <div
                              style={{
                                width: "12px",
                                height: "12px",
                                backgroundColor: "#28a745",
                                borderRadius: "50%",
                                marginRight: "8px",
                              }}
                            ></div>
                            <span>{props.t("Ponto de entrada")}</span>
                          </div>
                          <div
                            style={{ display: "flex", alignItems: "center" }}
                          >
                            <div
                              style={{
                                width: "12px",
                                height: "12px",
                                backgroundColor: "#dc3545",
                                borderRadius: "50%",
                                marginRight: "8px",
                              }}
                            ></div>
                            <span>{props.t("Ponto de saída")}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Location Data Table */}
              {locationData.length > 0 && (
                <div
                  className="history-table-container"
                  style={{ marginTop: "20px" }}
                >
                  <h3>{props.t("Pontos de Localização")}</h3>
                  <table className="table table-hover table-striped">
                    <thead>
                      <tr>
                        <th>{props.t("Data/Hora")}</th>
                        <th>{props.t("Tipo de Atividade")}</th>
                        <th>{props.t("Latitude")}</th>
                        <th>{props.t("Longitude")}</th>
                        <th>{props.t("Velocidade")}</th>
                        <th>{props.t("Precisão")}</th>
                        <th>{props.t("Bateria")}</th>
                        <th>{props.t("Ação")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {locationData.map((location, index) => (
                        <tr key={location.id || index}>
                          <td>
                            {formatTimestamp(location.timestamp)}
                            {location.device_model && (
                              <div style={{ fontSize: "11px", color: "#666" }}>
                                {location.device_model}
                              </div>
                            )}
                          </td>
                          <td>
                            <span className="badge badge-info">
                              {location.activity_type_display ||
                                location.activity_type ||
                                props.t("Desconhecido")}
                            </span>
                          </td>
                          <td>{location.latitude.toFixed(6)}</td>
                          <td>{location.longitude.toFixed(6)}</td>
                          <td>
                            {location.speed
                              ? `${location.speed.toFixed(1)} km/h`
                              : "-"}
                          </td>
                          <td>
                            {location.accuracy
                              ? `${location.accuracy.toFixed(1)} m`
                              : "-"}
                          </td>
                          <td>
                            {location.battery_level !== null &&
                            location.battery_level !== undefined ? (
                              <span
                                style={{
                                  color:
                                    location.battery_level < 20
                                      ? "#dc3545"
                                      : location.battery_level < 50
                                      ? "#ffc107"
                                      : "#28a745",
                                }}
                              >
                                {location.battery_level}%
                                {location.is_charging && " ⚡"}
                              </span>
                            ) : (
                              "-"
                            )}
                          </td>
                          <td>
                            <a
                              href={`https://www.google.com/maps?q=${location.latitude},${location.longitude}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-sm btn-primary"
                            >
                              {props.t("Ver no Google Maps")}
                            </a>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* No Data Message */}
              {!loading && selectedWorker && locationData.length === 0 && (
                <div style={{ textAlign: "center", marginTop: "50px" }}>
                  <h4>{props.t("Nenhum dado de localização encontrado")}</h4>
                  <p style={{ color: "#666", marginTop: "15px" }}>
                    {props.t(
                      "Não foram encontrados registros de localização para este trabalhador no período selecionado."
                    )}
                  </p>
                  <p
                    style={{
                      color: "#999",
                      fontSize: "14px",
                      marginTop: "10px",
                    }}
                  >
                    {props.t("Tente ajustar as datas ou filtros de atividade.")}
                  </p>
                </div>
              )}
            </>
          )}
        </>
      )}

      <ToastContainer />
    </div>
  );
}

export default withTranslation()(LocationHistory);
